import { TvaApplications } from "./tvaApplicability";

type WoodboxPrices = {
  bottleVolume: number;
  bottleQuantity: number;
  price: number;
  region: "EUROPE" | "EUROPEANUNION" | "OTHER" | "FRANCE";
}[];

type Product = {
  value: number;
  volume: number;
  bottleQuantity: number;
};

type WoodBox = {
  boxQuantity: number;
  bottleVolume: number;
  bottleQuantity: number;
};

type CotationData = {
  products: Product[];
  dapOption: boolean;
  insurance: boolean;
  express: boolean;
  otherFees?: number;
  woodBoxesActivated: boolean;
  woodBoxes: WoodBox[];
};

type CustomTarif = {
  id: string;
  label: string;
  isExpress: boolean;
  bottleTransportPrices: { quantity: number; amount: number }[];
};

type Custom = {
  taxPerShipment: number;
  wineValueTaxPercentage: number;
  vatPercentage: number;
  taxPerLiter: number;
  taxPerBottle: number;
  transportPlusWineValueTaxPercentage: number;
  transportPlusWinePlusInsuranceTaxPercentage: number;
  customTarifs: CustomTarif[];
  customWineValueTaxRanges: { from: number; to: number; value: number }[];
  bottleMaxPerShipment: number | null;
};

const insuranceFlatFee = 5;
const inusranceFlatFeethreshold = 500;

const roundToTwoDecimals = (value: number): number => {
  if (isNaN(value)) return 0;
  return parseFloat(value.toFixed(2));
};

export const getExpeditionPriceParameters = (
  expeditionPriceParameters: any,
) => {
  return {
    customTrimmed: expeditionPriceParameters.customTrimmed,
    insurancePercentage: expeditionPriceParameters.insurancePercentage,
    woodBoxMembranesPriceHT: expeditionPriceParameters.woodBoxMembranesPriceHT,
    woodboxPrices: expeditionPriceParameters.applicableWoodboxPriceTrimmed,
  };
};

export const calculateCotationPrice = (
  cotationData: CotationData,
  custom: Custom,
  woodBoxMembranesPriceHT: number,
  insurancePercentage: number,
  woodboxPrices: WoodboxPrices,
  tvaApplications: TvaApplications = {
    transport: true,
    insurance: true,
    woodBoxes: true,
    membranes: true,
  },
) => {
  const winePrice = cotationData.products.reduce((acc, product) => {
    return acc + product.value;
  }, 0);

  if (winePrice === 0) {
    return {
      transport: {
        transport: 0,
        transportHt: 0,
        tvaTransport: 0,
      },
      taxes: 0, // AKA droits douaniers (Not VAT)
      insurance: 0,
      woodBoxes: 0,
      otherFees: 0,
      otherFeesPercent: 0,
      transportPercent: 0,
      taxesPercent: 0,
      insurancePercent: 0,
      woodBoxesPercent: 0,
      totalPrice: 0,
      priceWWine: {
        transportPercent: 0,
        taxesPercent: 0,
        insurancePercent: 0,
        woodBoxesPercent: 0,
        winePercent: 0,
        otherFeesPercent: 0,
        totalPriceWWine: 0,
        winePrice: 0,
      },
      insuranceDetails: {
        insurancePrice: 0,
        insurancePriceHT: 0,
        insurancePriceTva: 0,
      },
      woodBoxDetails: {
        woodBoxTva: 0,
        woodBoxPriceHT: 0,
        woodBoxPrice: 0,
        membranesPrice: 0,
        membranesPriceHT: 0,
        membranesPriceTva: 0,
        isOffered: false,
      },
    };
  }

  const { transport, transportHt, tvaTransport } = calculateTransportPrice(
    custom,
    cotationData.products,
    cotationData.express,
    tvaApplications.transport,
  );

  const { insurancePrice, insurancePriceHT, insurancePriceTva } =
    getInsurancePrice(
      cotationData.products,
      cotationData.insurance,
      insurancePercentage,
      tvaApplications.insurance,
    );

  const taxes = calculateTaxesPrice(
    cotationData.dapOption,
    cotationData.products,
    custom,
    transportHt,
    insurancePriceHT,
  );

  const {
    woodBoxPriceTotal,
    woodBoxPriceHT,
    woodBoxPrice,
    woodBoxTva,
    membranesPrice,
    membranesPriceHT,
    membranesPriceTva,
    isOffered,
  } = getWoodBoxPrice(
    cotationData.woodBoxesActivated,
    cotationData.woodBoxes,
    woodBoxMembranesPriceHT,
    woodboxPrices,
    cotationData.products,
    tvaApplications.woodBoxes,
    tvaApplications.membranes,
  );

  const totalPrice =
    transport +
    taxes +
    insurancePrice +
    woodBoxPriceTotal +
    (cotationData?.otherFees || 0);

  const totalPriceWWine = totalPrice + winePrice;

  return {
    transport: {
      transport: roundToTwoDecimals(transport),
      transportHt: roundToTwoDecimals(transportHt),
      tvaTransport: roundToTwoDecimals(tvaTransport),
    },
    taxes: roundToTwoDecimals(taxes), // AKA droits douaniers (Not VAT)
    insurance: roundToTwoDecimals(insurancePrice),
    woodBoxes: roundToTwoDecimals(woodBoxPriceTotal),
    otherFees: roundToTwoDecimals(cotationData?.otherFees || 0),
    otherFeesPercent: Math.round(
      ((cotationData?.otherFees || 0) / totalPrice) * 100,
    ),
    transportPercent: Math.round((transport / totalPrice) * 100),
    taxesPercent: Math.round((taxes / totalPrice) * 100),
    insurancePercent: Math.round((insurancePrice / totalPrice) * 100),
    woodBoxesPercent: Math.round((woodBoxPriceTotal / totalPrice) * 100),
    totalPrice: roundToTwoDecimals(totalPrice),
    priceWWine: {
      transportPercent: Math.round((transport / totalPriceWWine) * 100),
      taxesPercent: Math.round((taxes / totalPriceWWine) * 100),
      insurancePercent: Math.round((insurancePrice / totalPriceWWine) * 100),
      woodBoxesPercent: Math.round((woodBoxPriceTotal / totalPriceWWine) * 100),
      winePercent: Math.round((winePrice / totalPriceWWine) * 100),
      otherFeesPercent: Math.round(
        ((cotationData?.otherFees || 0) / totalPriceWWine) * 100,
      ),
      totalPriceWWine: roundToTwoDecimals(totalPriceWWine),
      winePrice: roundToTwoDecimals(winePrice),
    },
    insuranceDetails: {
      insurancePrice: roundToTwoDecimals(insurancePrice),
      insurancePriceHT: roundToTwoDecimals(insurancePriceHT),
      insurancePriceTva: roundToTwoDecimals(insurancePriceTva),
    },
    woodBoxDetails: {
      woodBoxTva: roundToTwoDecimals(woodBoxTva),
      woodBoxPriceHT: roundToTwoDecimals(woodBoxPriceHT),
      woodBoxPrice: roundToTwoDecimals(woodBoxPrice),
      membranesPrice: roundToTwoDecimals(membranesPrice),
      membranesPriceHT: roundToTwoDecimals(membranesPriceHT),
      membranesPriceTva: roundToTwoDecimals(membranesPriceTva),
      isOffered,
    },
  };
};

// Calculate the transport price
const calculateTransportPrice = (
  custom: Custom,
  products: Product[],
  express: boolean,
  tvaTransportApplicable: boolean,
) => {
  const nbBottles = getBottle75clCount(products);

  if (!nbBottles) return { transport: 0, transportHt: 0, tvaTransport: 0 };

  let transportPrice = 0;

  const customTarif = custom.customTarifs.find((el) => el.isExpress === false);
  const customExpressTarif = custom.customTarifs.find(
    (el) => el.isExpress === true,
  );

  if (!customTarif || !customExpressTarif) {
    console.info("Custom tarif not found");
    return { transport: 0, transportHt: 0, tvaTransport: 0 };
  }

  const numberOfShipments = numberOfShipmentsNeeded(
    custom.bottleMaxPerShipment,
    products,
  );

  const selectClosestBottleTransportPrice = (
    btls: number,
    bottleTransportPrices: { quantity: number; amount: number }[],
  ) => {
    const sortedBottleTransportPrices = [...bottleTransportPrices].sort(
      (a, b) => a.quantity - b.quantity,
    );
    const nextUpcoming = sortedBottleTransportPrices.find(
      (el) => el.quantity >= btls,
    );
    if (nextUpcoming) return nextUpcoming;
    // if there are no transport prices
    if (sortedBottleTransportPrices.length === 0)
      return { quantity: 0, amount: 0 };
    return sortedBottleTransportPrices[sortedBottleTransportPrices.length - 1];
  };

  if (numberOfShipments > 1 && custom.bottleMaxPerShipment) {
    const selectedBottleTransport = selectClosestBottleTransportPrice(
      Number(custom.bottleMaxPerShipment),
      customTarif.bottleTransportPrices,
    );
    transportPrice += selectedBottleTransport.amount * (numberOfShipments - 1);
    const remainingBottles =
      nbBottles - Number(custom.bottleMaxPerShipment) * (numberOfShipments - 1);
    const remainingBottlesTransportPrice = selectClosestBottleTransportPrice(
      remainingBottles,
      customTarif.bottleTransportPrices,
    );
    transportPrice += remainingBottlesTransportPrice.amount;
  } else {
    const selectedBottleTransport = selectClosestBottleTransportPrice(
      nbBottles,
      customTarif.bottleTransportPrices,
    );
    transportPrice += selectedBottleTransport.amount;
  }

  if (express) {
    if (numberOfShipments > 1 && custom.bottleMaxPerShipment) {
      const selectedBottleTransport = selectClosestBottleTransportPrice(
        Number(custom.bottleMaxPerShipment),
        customExpressTarif.bottleTransportPrices,
      );
      transportPrice +=
        selectedBottleTransport.amount * (numberOfShipments - 1);
      const remainingBottles =
        nbBottles -
        Number(custom.bottleMaxPerShipment) * (numberOfShipments - 1);
      const remainingBottlesTransportPrice = selectClosestBottleTransportPrice(
        remainingBottles,
        customExpressTarif.bottleTransportPrices,
      );
      transportPrice += remainingBottlesTransportPrice.amount;
    } else {
      const selectedBottleTransport = selectClosestBottleTransportPrice(
        nbBottles,
        customExpressTarif.bottleTransportPrices,
      );
      transportPrice += selectedBottleTransport.amount;
    }
  }

  const tvaTransport = tvaTransportApplicable ? transportPrice * 0.2 : 0;

  return {
    transport: transportPrice + tvaTransport,
    transportHt: transportPrice,
    tvaTransport,
  };
};

const calculateTaxesPrice = (
  dapOption: boolean,
  products: Product[],
  custom: Custom,
  transportPrice: number,
  insurancePrice: number,
) => {
  if (dapOption) return 0;

  const totalWineValue = getTotalWineValue(products);

  let taxes = 0;
  // Shipment tax
  taxes +=
    numberOfShipmentsNeeded(custom.bottleMaxPerShipment, products) *
    custom.taxPerShipment;

  // Wine value tax
  taxes += (custom.wineValueTaxPercentage / 100) * totalWineValue;

  // Tax sur le litre de vin
  taxes += custom.taxPerLiter * getTotalLitersWine(products);

  // Tax sur les bouteilles de vin
  taxes += custom.taxPerBottle * getBottle75clCount(products);

  // Tax percent transport + valeur du vin
  taxes +=
    (custom.transportPlusWineValueTaxPercentage / 100) *
    (transportPrice + totalWineValue);

  const sortedWineValueTaxRanges = [...custom.customWineValueTaxRanges].sort(
    (a, b) => a.from - b.from,
  );
  // Tax wine value range
  const applicableRange =
    totalWineValue === 0
      ? sortedWineValueTaxRanges[0]?.value || 0
      : (sortedWineValueTaxRanges.find(
          (range) => range.from < totalWineValue && range.to >= totalWineValue,
        )?.value ??
        sortedWineValueTaxRanges[sortedWineValueTaxRanges.length - 1]?.value ??
        0);
  taxes += applicableRange;

  // Tax vin, transport, assurance
  taxes +=
    (custom.transportPlusWinePlusInsuranceTaxPercentage / 100) *
    (totalWineValue + transportPrice + insurancePrice);

  return taxes;
};

const getInsurancePrice = (
  products: Product[],
  insurance: boolean,
  insurancePercentage: number,
  tvaInsuranceApplicable: boolean,
): {
  insurancePrice: number;
  insurancePriceHT: number;
  insurancePriceTva: number;
} => {
  if (!insurance)
    return { insurancePrice: 0, insurancePriceHT: 0, insurancePriceTva: 0 };

  const totalWineValue = getTotalWineValue(products);
  const insurancePriceHT =
    totalWineValue <= inusranceFlatFeethreshold
      ? insuranceFlatFee
      : (totalWineValue * insurancePercentage) / 100;
  const insurancePriceTva = tvaInsuranceApplicable ? insurancePriceHT * 0.2 : 0;

  return {
    insurancePrice: insurancePriceHT + insurancePriceTva,
    insurancePriceHT,
    insurancePriceTva,
  };
};

const getWoodBoxPrice = (
  woodBoxesActivated: boolean,
  woodBoxes: WoodBox[],
  pricePerMembraneHT: number,
  woodboxPrices: WoodboxPrices,
  products: Product[],
  tvaWoodBoxesApplicable: boolean,
  tvaMembranesApplicable: boolean,
): {
  woodBoxPriceTotal: number; // Wood box price total including tva (wood box price + membranes price)
  woodBoxPrice: number;
  woodBoxPriceHT: number;
  woodBoxTva: number;
  membranesPrice: number;
  membranesPriceHT: number;
  membranesPriceTva: number;
  isOffered: boolean;
} => {
  if (woodBoxesActivated === false)
    return {
      woodBoxPriceTotal: 0,
      woodBoxPrice: 0,
      woodBoxPriceHT: 0,
      woodBoxTva: 0,
      membranesPrice: 0,
      membranesPriceHT: 0,
      membranesPriceTva: 0,
      isOffered: false,
    };

  const isOffered =
    Array.isArray(woodboxPrices) &&
    woodboxPrices.length > 0 &&
    isSentAsPalette(products, woodboxPrices[0].region);

  if (isOffered) {
    return {
      woodBoxPriceTotal: 0,
      woodBoxPriceHT: 0,
      woodBoxPrice: 0,
      woodBoxTva: 0,
      membranesPrice: 0,
      membranesPriceHT: 0,
      membranesPriceTva: 0,
      isOffered: true,
    };
  }
  const woodBoxPriceHT = woodBoxes.reduce(
    (acc, box) =>
      acc +
      (woodboxPrices.find(
        (e) =>
          e.bottleVolume === box.bottleVolume &&
          e.bottleQuantity === box.bottleQuantity,
      )?.price || 0) *
        box.boxQuantity,
    0,
  );
  const woodBoxTva = tvaWoodBoxesApplicable ? woodBoxPriceHT * 0.2 : 0;
  const woodBoxPrice = woodBoxPriceHT + woodBoxTva;
  const membranesCount = getMembranesCount(woodBoxes);
  const membranesPriceHT = pricePerMembraneHT * membranesCount;
  const membranesPriceTva = tvaMembranesApplicable ? membranesPriceHT * 0.2 : 0;
  const membranesPrice = membranesPriceHT + membranesPriceTva;

  const woodBoxPriceTotal = woodBoxPrice + membranesPrice;

  return {
    woodBoxPriceTotal,
    woodBoxPriceHT,
    woodBoxPrice,
    woodBoxTva,
    membranesPrice,
    membranesPriceHT,
    membranesPriceTva,
    isOffered: false,
  };
};

// Number of shipments needed to transport all bottles based on the custom bottle transport prices grid
export const numberOfShipmentsNeeded = (
  bottleMaxPerShipment: number | null,
  products: Product[],
) => {
  if (!bottleMaxPerShipment) return 1;
  const nbBottles75cl = getBottle75clCount(products);

  return Math.ceil(nbBottles75cl / Number(bottleMaxPerShipment));
};

// 1 bottle 75cl count (1.5L equal 2 bottles)
const getBottle75clCount = (products: Product[]) => {
  return (products || []).reduce((acc, product) => {
    if (!product.volume) return acc;
    const volume = product.volume;
    const bottleQuantity = product.bottleQuantity;
    const nb75clBottles = (bottleQuantity * volume) / 0.75;
    return acc + nb75clBottles;
  }, 0);
};

// Get the total wine value of all products
const getTotalWineValue = (products: { value: number }[]) => {
  return (products || []).reduce((acc, product) => acc + product.value, 0);
};

const getTotalLitersWine = (
  products: { volume: number; bottleQuantity: number }[],
) => {
  return (products || []).reduce((acc, product) => {
    const volume = product.volume;
    const bottleQuantity = product.bottleQuantity;
    const totalVolume = volume * bottleQuantity;
    return acc + totalVolume;
  }, 0);
};

const getMembranesCount = (
  woodBoxes: {
    boxQuantity: number;
    bottleQuantity: number;
    bottleVolume: number;
  }[],
) => {
  return (woodBoxes || []).reduce(
    (acc, box) =>
      acc +
      (isMembraneNeeded(box.bottleQuantity, box.bottleVolume)
        ? box.boxQuantity
        : 0),
    0,
  );
};

// no membranes for woodbox of 1 bottles of 0.75L or 1.5L
const isMembraneNeeded = (
  bottleQuantity: number,
  bottleVolume: number,
): boolean => {
  return !(
    bottleQuantity === 1 &&
    (bottleVolume === 0.75 || bottleVolume === 1.5)
  );
};

// 90 liters for France and 18 otherwise
export const isSentAsPalette = (
  products: Product[],
  woodboxRegion: string,
): boolean => {
  const productsShortListed = excludeProductsNotSentAsPalette(
    products,
    woodboxRegion,
  );
  const orderWineVolume = calculateOrderWineVolume(productsShortListed);
  if (woodboxRegion === "FRANCE") return orderWineVolume > 90;
  return orderWineVolume > 18;
};

// for France only bottles of 3L and 6L have a free shipping option
const excludeProductsNotSentAsPalette = (
  products: Product[],
  woodboxRegion: string,
): Product[] => {
  if (woodboxRegion !== "FRANCE") return products;
  return products.filter(
    (product) => product.volume === 3 || product.volume === 6,
  );
};

const calculateOrderWineVolume = (products: Product[]) => {
  return products.reduce(
    (acc, product) => acc + product.volume * product.bottleQuantity,
    0,
  );
};
